import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';


export default function Members() {
  const { id: groupIdParam } = useParams();
  const groupId = Number(groupIdParam);

  const me = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const meId = Number(me?.id || 0);

  const [members, setMembers] = useState([]);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr('');
      try {
        // Load group (optional, for title) and group members
        const [gRes, mRes] = await Promise.all([
          apiFetch(`/api/groups/${groupId}`).then(r => r.json()).catch(() => null),
          apiFetch(`/api/groups/${groupId}/members`).then(r => r.json()),
        ]);

        if (!alive) return;

        setGroup(gRes || null);

        // Be tolerant of array vs wrapped shapes
        const rows =
          Array.isArray(mRes) ? mRes :
          Array.isArray(mRes?.members) ? mRes.members :
          Array.isArray(mRes?.rows) ? mRes.rows :
          [];

        setMembers(rows);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to fetch members');
        setMembers([]);
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (groupId) load();

    return () => { alive = false; };
  }, [groupId]);

  const title = useMemo(() => {
    if (group?.name) return `Members in ${group.name}`;
    return `Members in Group #${groupId}`;
  }, [group, groupId]);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Logo row, same as other pages */}
      <div className="flex justify-center mb-4">
        <img src="/logo-horizontal.svg" alt="ChitConnect" style={{ height: 36 }} />
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b bg-[#f5fbff] flex items-center justify-between">
          <h1 className="text-xl font-semibold">{title}</h1>
          <Link to="/groups" className="text-sm text-blue-700 hover:underline">← Back to Groups</Link>
        </div>

        {/* No Bank details section on this page */}

        {/* Body */}
        <div className="p-4">
          {loading && (
            <div className="text-gray-600">Loading…</div>
          )}

          {!loading && err && (
            <div className="text-red-600">Server error while fetching members.</div>
          )}

          {!loading && !err && members.length === 0 && (
            <div className="text-gray-600">No members found.</div>
          )}

          {!loading && !err && members.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full border">
                <thead className="bg-[#0e3e68] text-white">
                  <tr>
                    <th className="text-left px-3 py-2 w-24">#</th>
                    <th className="text-left px-3 py-2">Member</th>
                    <th className="text-left px-3 py-2 w-32">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => {
                    // be tolerant of different shapes coming from API
                    const idx = m.membership_index ?? m.index ?? (i + 1);
                    const name =
                      (m.full_name?.trim?.() ||
                      [m.given_name, m.surname].filter(Boolean).join(' ').trim() ||
                      m.name ||
                      `Member ${idx}`);
                    const uid = Number(m.user_id || m.id || 0);
                    return (
                      <tr key={`${idx}-${uid}-${i}`} className="border-t">
                        <td className="px-3 py-2">{idx}</td>
                        <td className="px-3 py-2">{name}</td>
                        <td className="px-3 py-2">
                          {uid && uid !== meId ? (
                            <Link
                              to={`/messages/dm/u/${uid}`}
                              className="inline-flex items-center justify-center px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                            >
                              Message
                            </Link>
                          ) : (
                            <span className="text-sm text-gray-500">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
